import { isValidDate, isValidMonth, monthStart, personalDateOf, weekStartOf } from '@wiztime/core';
import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router';
import { RoutePattern } from '../app/routes';
import { useNow } from './useNow';
import { useSettings } from './useSettings';

export const ViewKind = {
  Day: 'day',
  Week: 'week',
  Month: 'month',
  Stats: 'stats',
  Settings: 'settings',
  Other: 'other',
} as const;
export type ViewKind = (typeof ViewKind)[keyof typeof ViewKind];

export interface RouteView {
  readonly kind: ViewKind;
  /** The personal date of the day view, the week view's first day or the month view's 1st; null elsewhere. */
  readonly date: string | null;
}

/** Today's personal date, following the configured day start and the clock. */
export function useToday(): string {
  const settings = useSettings();
  const now = useNow(60_000);
  return personalDateOf(now, settings.dayStartMin);
}

function validDate(value: string | undefined): string | null {
  return value !== undefined && isValidDate(value) ? value : null;
}

/** Which top-level view a route path (`/day/2026-10-07`) shows, and the date it is on. */
export function routeViewOf(pathname: string): RouteView {
  const day = matchPath(RoutePattern.Day, pathname);
  const week = matchPath(RoutePattern.Week, pathname);
  const month = matchPath(RoutePattern.Month, pathname);
  const stats = matchPath(RoutePattern.Stats, pathname);
  const settings = matchPath(RoutePattern.Settings, pathname);
  if (day !== null) return { kind: ViewKind.Day, date: validDate(day.params.date) };
  if (week !== null) {
    const start = validDate(week.params.weekStart);
    return { kind: ViewKind.Week, date: start === null ? null : weekStartOf(start) };
  }
  if (month !== null) {
    const value = month.params.month;
    return {
      kind: ViewKind.Month,
      date: value !== undefined && isValidMonth(value) ? monthStart(value) : null,
    };
  }
  if (stats !== null) return { kind: ViewKind.Stats, date: null };
  if (settings !== null) return { kind: ViewKind.Settings, date: null };
  return { kind: ViewKind.Other, date: null };
}

/** Which top-level view the current route shows, and the date it is on. */
export function useRouteView(): RouteView {
  const { pathname } = useLocation();
  return useMemo(() => routeViewOf(pathname), [pathname]);
}
